import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useInView } from '@/hooks/useInView';

interface StatItemProps {
  target: number;
  label: string;
  suffix: string;
  decimals?: number;
  started: boolean;
}

const StatItem: React.FC<StatItemProps> = ({ target, label, suffix, decimals = 0, started }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!started) return;

    const duration = 1200;
    const steps = 40;
    const increment = target / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current = Math.min(target, increment * step);
      setValue(current);
      if (step >= steps) {
        clearInterval(timer);
        setValue(target);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [started, target]);

  const displayValue = decimals > 0 ? value.toFixed(decimals) : Math.round(value);

  return (
    <Box sx={{ textAlign: 'center', px: { xs: 2, md: 4 } }}>
      <Typography variant="h3" component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {displayValue}
      </Typography>
      <Typography variant="h3" component="span" sx={{ fontWeight: 700, color: '#f59842' }}>
        {suffix}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );
};

export const StatsStrip: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [ref, inView] = useInView({ threshold: 0.3 });

  const stats = [
    { target: 500, label: 'properties', suffix: '+' },
    { target: 50, label: 'destinations', suffix: '+' },
    { target: 4.8, label: 'average rating', suffix: '★', decimals: 1 },
  ];

  return (
    <Box ref={ref} sx={{ py: { xs: 5, md: 8 } }}>
      <Container maxWidth="md" sx={{ px: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? 4 : 0,
          }}
        >
          {stats.map((stat, index) => (
            <React.Fragment key={stat.label}>
              {index > 0 && !isMobile && (
                <Box sx={{ width: 1, height: 48, bgcolor: 'divider', mx: 2 }} />
              )}
              <StatItem
                target={stat.target}
                label={stat.label}
                suffix={stat.suffix}
                decimals={stat.decimals}
                started={inView}
              />
            </React.Fragment>
          ))}
        </Box>
      </Container>
    </Box>
  );
};
